import { jsx, jsxs } from 'react/jsx-runtime';
import { createContext, useContext } from 'react';
import classNames from 'classnames';
import { Icon } from '@ln/common-ui-icon';
import { Text } from '@ln/common-ui-text';
import { Button } from '@ln/experiencias-ui-button';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const CardupsellingContext = createContext({
    variant: 'default',
    plan: undefined
});
const CardupsellingContextProvider = ({ children, variant = 'default', plan, }) => {
    return (jsx(CardupsellingContext.Provider, Object.assign({ value: { variant, plan } }, { children: children })));
};
const useCardupsellingContext = () => useContext(CardupsellingContext);

function CardupsellingChip(_a) {
    var { className, children, containerClassName } = _a, props = __rest(_a, ["className", "children", "containerClassName"]);
    const { variant } = useCardupsellingContext();
    const _containerClassName = classNames('grid card-upselling-chip transition-regular overflow-hidden', variant === 'default' ? 'visibility-hidden grid-rows-0' : 'visibility-visible grid-rows-1', containerClassName);
    const _className = classNames('relative transition-regular flex jc-center ai-center px-16 py-4 arial-bold text-16', { 'bg-neutral-light-100': variant === 'actual' }, { 'bg-orange-400': variant === 'selected' }, className);
    return (jsx("div", Object.assign({ className: _containerClassName }, { children: jsx("div", Object.assign({ className: 'overflow-hidden' }, { children: jsx("div", Object.assign({ className: _className }, props, { children: children })) })) })));
}

function CardupsellingBody(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    const _className = classNames('flex flex-column gap-16 p-16 bg-neutral-light-1 mx-2 mb-2 rounded-bottom-left-inherit rounded-bottom-right-inherit', className);
    return (jsx("div", Object.assign({ className: _className }, props)));
}

const CardupsellingPrice = ({ price }) => {
    return (jsxs("div", { children: [jsx(Text, Object.assign({ className: 'prumo prumo-semibold text-18 text-32_md' }, { children: "$" })), jsx(Text, Object.assign({ className: 'prumo prumo-semibold text-32 text-72_md' }, { children: price })), jsx(Text, Object.assign({ className: 'prumo prumo-semibold text-16' }, { children: "/mes" }))] }));
};

function CardupsellingHeader(_a) {
    var { className, planName, price, details, description, credentialIcon, credentialIconProps } = _a, props = __rest(_a, ["className", "planName", "price", "details", "description", "credentialIcon", "credentialIconProps"]);
    const { variant, plan } = useCardupsellingContext();
    const _className = classNames('flex ai-center flex-column_md gap-16', className);
    const planClassName = classNames('prumo text-24 text-28_md', variant === 'selected' ? 'prumo-medium' : 'prumo-book');
    return (jsxs("div", Object.assign({ className: _className }, props, { children: [jsxs("div", Object.assign({ className: 'flex ai-center jc-between w-100 gap-16 h-50_md' }, { children: [jsx(Text, Object.assign({ className: planClassName }, { children: planName })), credentialIcon && plan && (jsx(Icon, Object.assign({ height: 50, className: 'md-only' }, credentialIconProps, { children: credentialIcon })))] })), jsxs("div", Object.assign({ className: 'flex flex-column gap-4' }, { children: [jsx(CardupsellingPrice, { price: price }), jsxs("div", Object.assign({ className: 'flex flex-column ai-center gap-4 text-12 h-44_md' }, { children: [details && jsx(Text, Object.assign({ className: 'arial-bold text-16_md' }, { children: details })), description && jsx(Text, Object.assign({ className: 'md-only' }, { children: description }))] }))] }))] })));
}

function CardupsellingButton(_a) {
    var { iconProps, children } = _a, props = __rest(_a, ["iconProps", "children"]);
    const { plan, variant } = useCardupsellingContext();
    const getVariantType = (variant, plan) => {
        if (variant === 'selected')
            return 'secondary';
        if (plan === 'black')
            return 'black';
        return 'primary';
    };
    const getIcon = (variant) => {
        if (variant === 'selected')
            return (jsx("svg", Object.assign({ xmlns: "http://www.w3.org/2000/svg", width: "17", height: "16", viewBox: "0 0 17 16", fill: "none" }, { children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M6.91454 10.115L12.5712 4.4577C12.8315 4.19735 13.2536 4.19726 13.5141 4.45751V4.45751C13.7746 4.71788 13.7747 5.14019 13.5142 5.40065L7.62165 11.2932C7.23112 11.6837 6.59796 11.6837 6.20743 11.2932L3.14321 8.229C2.8829 7.96869 2.8829 7.54664 3.14321 7.28633V7.28633C3.40352 7.02602 3.82556 7.02602 4.08588 7.28633L6.91454 10.115Z", fill: "#333333" }) })));
        if (variant === 'default')
            return (jsx("svg", Object.assign({ xmlns: "http://www.w3.org/2000/svg", width: "17", height: "16", viewBox: "0 0 17 16", fill: "none" }, { children: jsx("path", { d: "M8.08594 7.33301V3.99967C8.08594 3.63148 8.38441 3.33301 8.7526 3.33301C9.12079 3.33301 9.41927 3.63148 9.41927 3.99967V7.33301H12.7526C13.1208 7.33301 13.4193 7.63148 13.4193 7.99967C13.4193 8.36786 13.1208 8.66634 12.7526 8.66634H9.41927V11.9997C9.41927 12.3679 9.12079 12.6663 8.7526 12.6663C8.38441 12.6663 8.08594 12.3679 8.08594 11.9997V8.66634H4.7526C4.38441 8.66634 4.08594 8.36786 4.08594 7.99967C4.08594 7.63148 4.38441 7.33301 4.7526 7.33301H8.08594Z", fill: "#FEFEFE" }) })));
        return undefined;
    };
    const buttonVariant = getVariantType(variant, plan);
    const icon = getIcon(variant);
    return (jsxs(Button, Object.assign({ fullWidth: true, disabled: variant === 'actual', variant: buttonVariant }, props, { children: [icon && jsx(Icon, Object.assign({ size: 16 }, iconProps, { children: icon })), children] })));
}

function Cardupselling(_a) {
    var { variant = 'default', plan, className, children } = _a, props = __rest(_a, ["variant", "plan", "className", "children"]);
    const cardColors = {
        actual: 'outline-neutral-light-200 outline-dashed',
        selected: 'outline-neutral-light-100 shadow-float',
        default: 'outline-neutral-light-100'
    };
    const _className = classNames('card-upselling w-100 rounded-16 overflow-hidden relative', { 'outline outline-2 -outline-offset-2 outline-solid': variant !== 'selected' }, cardColors[variant], className);
    return (jsx(CardupsellingContextProvider, Object.assign({ variant: variant, plan: plan }, { children: jsxs("article", Object.assign({ className: _className }, props, { children: [variant === 'selected' && jsx("div", { className: '--animated-border' }), children] })) })));
}
Cardupselling.Chip = CardupsellingChip;
Cardupselling.Body = CardupsellingBody;
Cardupselling.Header = CardupsellingHeader;
Cardupselling.Button = CardupsellingButton;

export { Cardupselling };
